import type { CanvasDoc, Effect, Frame, FreeFrame } from '../store/types';
import { migrateCfIds } from './migrateCfIds';
export { encodeDoc } from '@hudak/canvas-core';

export class SchemaError extends Error {}

export function decodeDoc(raw: string): CanvasDoc {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    throw new SchemaError('Invalid JSON');
  }
  if (typeof parsed !== 'object' || parsed === null) {
    throw new SchemaError('Not an object');
  }
  const p = parsed as Record<string, unknown>;
  if (p.version === 1) return migrateCfIds(recoverLegacyChats(migrateV1(p)));
  if (p.version === 2) {
    if (!Array.isArray(p.frames)) throw new SchemaError('frames must be an array');
    if (typeof p.viewport !== 'object' || p.viewport === null) {
      throw new SchemaError('viewport missing');
    }
    const doc = p as unknown as CanvasDoc;
    // Defensive: the canvas reads viewport.pan.x. A doc seeded with a flat
    // {x,y,zoom} viewport (no nested pan) would crash the renderer — coerce it.
    const vp = doc.viewport as unknown as { pan?: { x: number; y: number }; x?: number; y?: number; zoom?: number };
    if (!vp.pan) doc.viewport = { pan: { x: vp.x ?? 0, y: vp.y ?? 0 }, zoom: vp.zoom ?? 1 };
    return migrateCfIds(recoverLegacyChats(doc));
  }
  throw new SchemaError(`Unsupported version: ${String(p.version)}`);
}

/**
 * Conversation panel migration: chats once lived as standalone on-canvas nodes
 * (non-panel-owned). They no longer render on canvas — conversations live in the
 * docked panel + as chips. Recover any legacy on-canvas chat that holds real
 * messages so its history resurfaces, keeping the panel's one-chat-per-scope
 * invariant (one chat per anchored frame + one global):
 *
 * - scope already has a panel-owned chat → merge the ghost's transcript into it
 *   (ordered by message ts) and drop the ghost frame
 * - scope has none → promote the ghost to panel-owned (anchored → frame chat +
 *   chip; unanchored → the whole-canvas thread)
 * - anchored to a frame that no longer exists → recovered into the global scope
 *
 * Empty legacy chats are left alone (harmless, invisible). Returns the same doc
 * if unchanged.
 */
function recoverLegacyChats(doc: CanvasDoc): CanvasDoc {
  type Chat = Extract<Frame, { kind: 'chat' }>;
  const isGhost = (f: Frame): f is Chat =>
    f.kind === 'chat' && !f.panelOwned && Array.isArray(f.messages) && f.messages.length > 0;
  if (!doc.frames.some(isGhost)) return doc;

  const frameIds = new Set(doc.frames.map((f) => f.id));
  // Scope key: anchor id when the anchor still exists, '' = global.
  const scopeOf = (c: Chat): string => (c.anchorId && frameIds.has(c.anchorId) ? c.anchorId : '');
  const byTs = (a: Chat['messages'][number], b: Chat['messages'][number]) =>
    (a.ts ?? 0) - (b.ts ?? 0);

  // Existing panel-owned chats per scope (first one wins, matching findPanelFrame).
  const panelByScope = new Map<string, Chat>();
  for (const f of doc.frames) {
    if (f.kind === 'chat' && f.panelOwned) {
      const key = f.anchorId ?? '';
      if (!panelByScope.has(key)) panelByScope.set(key, f);
    }
  }

  const mergedInto = new Map<string, Chat['messages']>(); // panel chat id → merged messages
  const promoted = new Map<string, Chat>(); // ghost id → promoted frame
  const dropped = new Set<string>();

  for (const f of doc.frames) {
    if (!isGhost(f)) continue;
    const scope = scopeOf(f);
    const target = panelByScope.get(scope);
    if (target) {
      const acc = mergedInto.get(target.id) ?? [...target.messages];
      mergedInto.set(target.id, [...acc, ...f.messages].sort(byTs));
      dropped.add(f.id);
    } else {
      const promotedChat: Chat = {
        ...f,
        panelOwned: true,
        pinned: true,
        anchorId: scope || undefined,
      };
      promoted.set(f.id, promotedChat);
      panelByScope.set(scope, promotedChat); // later ghosts in this scope merge in
    }
  }

  const frames = doc.frames
    .filter((f) => !dropped.has(f.id))
    .map((f) => {
      const p = promoted.get(f.id);
      const base = p ?? f;
      const msgs = mergedInto.get(f.id);
      return msgs ? ({ ...base, messages: msgs } as Frame) : (base as Frame);
    });
  return { ...doc, frames };
}

function migrateV1(p: Record<string, unknown>): CanvasDoc {
  const v1Frames = (p.frames as Array<Record<string, unknown>>) ?? [];
  const frames: Frame[] = v1Frames.map((f): FreeFrame => ({
    kind: 'free',
    id: String(f.id),
    x: Number(f.x),
    y: Number(f.y),
    w: Number(f.w),
    h: f.h !== undefined ? Number(f.h) : undefined,
    z: Number(f.z),
    title: String(f.title ?? ''),
    html: String(f.html ?? ''),
    effect: migrateEffect(f.effect as Record<string, unknown> | undefined),
  }));
  const viewport = (p.viewport as CanvasDoc['viewport']) ?? { pan: { x: 0, y: 0 }, zoom: 1 };
  return { version: 2, viewport, frames };
}

function migrateEffect(e: Record<string, unknown> | undefined): Effect {
  if (!e) return { kind: 'none' };
  const intensity = typeof e.intensity === 'number' ? e.intensity : 0;
  switch (e.kind) {
    case 'none':
      return { kind: 'none' };
    case 'blur':
      return { kind: 'blur', px: intensity * 10 };
    case 'grayscale':
      return { kind: 'grayscale', amt: Math.min(1, Math.max(0, intensity)) };
    case 'glow':
      return {
        kind: 'glow',
        color: '#ffffff',
        radius: Math.max(4, intensity * 20),
        passes: 2,
      };
    case 'crt':
      return { kind: 'crt', intensity };
    case 'chromatic':
      return { kind: 'chromatic', offset: intensity * 4 };
    default:
      return { kind: 'none' };
  }
}
